import { Back, Power1, TweenMax } from 'gsap';
import { Sprite } from 'pixi.js';
import { GameAudio, getStarSound } from '../../../Config/Audio';
import SoundManager from '../../../Sound/SoundManager';
import { IRewardItem } from '../../../State/reducers/score';
import { SkippableAnimation } from '../../Animations/SkippableAnimation';
import { Star } from '../../Animations/Star';
import { RewardBox } from './RewardBox';
import { RewardGroup } from './RewardGroup';
import { WinScreenScore } from './WinScreenScore';
import { LevelActions, LevelEmitter } from '../../../Managers/Level/LevelEmitter';

export class RewardBanner extends Sprite {
    public rewardValues: IRewardItem[];
    private rewards: Array<RewardGroup | RewardBox> = [];
    private stars: Sprite[] = [];
    private slots: Sprite[] = [];
    private fxs: Star[] = [];
    private scoreDisplay: WinScreenScore;
    private openAnimation: SkippableAnimation;

    constructor(
        rewards: IRewardItem[],
        score: number,
        collectableInProgress: string,
        collectableProgress: number
    ) {
        super();

        this.rewardValues = rewards;
        const bg = new Sprite(PIXI.utils.TextureCache['gameui/header-blue']);
        bg.anchor.set(0.5);

        while (this.slots.length < 3) {
            const slot = new Sprite(PIXI.utils.TextureCache['gameui/star-slot']);
            slot.anchor.set(0.5);
            slot.x = ((bg.width * 0.9) / 3) * (this.slots.length - 1);
            // s.y = bg.height * 0.5;
            const slotscale = this.slots.length === 1 ? 1.15 : 1;
            slot.scale.set(slotscale);
            slot.rotation = 15 * (Math.PI / 180) * (this.slots.length - 1);
            this.slots.push(slot);
        }
        rewards.forEach((r, index) => {
            const s = new Sprite(PIXI.utils.TextureCache['gameui/popup-star']);
            s.anchor.set(0.5);
            s.x = ((bg.width * 0.9) / 3) * (index - 1);
            // s.y = bg.height * 0.5;
            const scale = index === 1 ? 1.15 : 1;
            s.scale.set(scale);
            s.rotation = 15 * (Math.PI / 180) * (index - 1);
            s.alpha = 0;
            this.stars.push(s);

            const reward =
                r.id !== 'Box'
                    ? new RewardGroup(r.id, r.val, collectableInProgress, collectableProgress)
                    : new RewardBox(
                          r.val as IRewardItem[],
                          'standard',
                          collectableInProgress,
                          collectableProgress
                      );
            reward.x = s.x;
            reward.y = r.id !== 'Box' ? s.y : s.y - bg.height * 0.3;
            if (r.id === 'Box') {
                reward.scale.set(0.8);
            } else {
                // const coinscale = r.val > 10 ? 1.5 : 2.5;
                reward.scale.set(1);
            }
            this.rewards.push(reward);
            const fx = new Star(s.scale.x * 2);
            const fxSprite = fx.sprite;
            fxSprite.x = s.x;
            fxSprite.y = s.y;
            fxSprite.rotation = s.rotation;
            this.fxs.push(fx);
        });

        this.scoreDisplay = new WinScreenScore(score);
        this.addChild(
            bg,
            ...this.slots,
            ...this.stars,
            ...this.rewards,
            ...this.fxs.map(fx => fx.sprite),
            this.scoreDisplay
        );

        this.scale.set(0);
        this.createOpenAnimation();
    }

    public async openBanner() {
        await this.openAnimation.play();
        return 'rewards shown';
    }

    public async showBest() {
        TweenMax.to(this.getChildAt(0), 0.8, {
            y: this.getChildAt(0).y - 300,
            ease: Power1.easeIn,
        });
        this.stars.forEach(s => {
            TweenMax.to(s, 0.8, { y: s.y - 300, ease: Power1.easeIn });
        });

        this.slots.forEach(s => {
            TweenMax.to(s, 0.8, { y: s.y - 300, ease: Power1.easeIn });
        });

        const targetScale =
            this.rewardValues[this.rewardValues.length - 1].id === 'Box' ? 1.8 : 1.8;
        SoundManager.playSound(GameAudio.REWARD_MOVE);
        TweenMax.to(this.rewards[this.rewards.length - 1].scale, 0.8, {
            x: targetScale,
            y: targetScale,
            ease: Power1.easeIn,
        });
        TweenMax.to(this.rewards[this.rewards.length - 1], 0.8, {
            x: 0,
            y: -75,
            ease: Power1.easeIn,
            onComplete: async () => {
                return await this.displayBest();
            },
        });
    }

    public async displayBest() {
        if (this.rewards.length === 3 && this.rewardValues[2].id === 'Box') {
            this.rewards[this.rewards.length - 1].initBox();
        } else {
            LevelEmitter.instance.emit(LevelActions.FINAL_REWARD, this.rewardValues);
        }

        return 'complete';
    }

    public removeBest() {
        TweenMax.to(this.rewards[this.rewards.length - 1], 0.3, {
            alpha: 0,
        });
    }

    public dismissBanner() {
        TweenMax.to(this.getChildAt(0), 0.5, { y: -(this.y + 100), ease: Power1.easeIn });
        TweenMax.to(this.scoreDisplay, 0.5, {
            y: -(this.y + 100),
            ease: Power1.easeIn,
            delay: 0.2,
        });

        this.stars.forEach(s => {
            TweenMax.to(s, 0.5, { y: -(this.y + 100), ease: Power1.easeIn });
        });

        this.slots.forEach(s => {
            TweenMax.to(s, 0.5, { y: -(this.y + 100), ease: Power1.easeIn });
        });
    }

    private createOpenAnimation() {
        const anim = (this.openAnimation = new SkippableAnimation());
        anim.add(() => this.scale.set(0.8, 1));

        anim.addToLast(
            TweenMax.to(this.scale, 0.25, {
                x: 1,
                y: 1,
                ease: Back.easeOut,
            })
        );
        anim.addDelay(600);

        for (let i = 0; i < this.rewards.length; i++) {
            const reward = this.rewards[i];
            const star = this.stars[i];
            anim.add(() => reward.showFX());
            anim.addToLast(TweenMax.to(reward, 0.6, { y: reward.y - 140, ease: Power1.easeOut }));
            anim.addToLast(
                TweenMax.to(reward.scale, 0.6, { x: reward.scale.x * 1.5, y: reward.scale.x * 1.5 })
            );

            anim.addToLast(TweenMax.to(star, 0.3, { alpha: 1 }));
            anim.addToLast(() => {
                const fx = this.fxs[i];
                fx.play();
                SoundManager.playSound(getStarSound(i + 1));
            });

            anim.add(() => (this.slots[i].alpha = 0));
        }

        anim.addDelay(1000);

        for (let i = 0; i < this.rewards.length - 1; i++) {
            const f = this.rewards[i];
            anim.add(TweenMax.to(f, 0.3, { alpha: 0, ease: Power1.easeOut }));
        }
    }
}
